import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from '../services/api';
import useAuthStore from '../store/authStore';
import BrandPanel from '../components/BrandPanel';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Button, Input, Field } from '../components/ui';
import { getApiErrorMessage } from '../utils/apiErrors';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { t } = useTranslation();
  const successMessage = location.state?.message;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(email, password);
      const { access, refresh, user } = response.data;

      setAuth(user, access, refresh);
      const pendingEnrollModule = localStorage.getItem('lmsid_pending_enroll_module');
      navigate(location.state?.from || (pendingEnrollModule ? '/store' : '/dashboard'));
    } catch (err) {
      setError(getApiErrorMessage(err, t, 'Login failed. Please check your credentials.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-surface-50">
      <BrandPanel />

      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div
          className="absolute right-4 sm:right-6"
          style={{ top: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
        >
          <LanguageSwitcher />
        </div>

        <div className="max-w-sm w-full">
          <Link to="/" className="mb-8 lg:hidden inline-block">
            <Logo size={36} textColor="text-gray-900" />
          </Link>

          <h2 className="text-2xl font-bold font-display tracking-tight text-gray-900">{t('Login')}</h2>
          <p className="text-sm text-gray-500 mt-1 mb-8">{t('Welcome back — pick up right where you left off.')}</p>

          {successMessage && !error && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg mb-5 text-sm">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label={t('Email')} required>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </Field>

            <Field label={t('Password')} required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>

            <div className="flex justify-end -mt-2">
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:underline">
                {t('Forgot password?')}
              </Link>
            </div>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              {loading ? t('Logging in...') : t('Login')}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-gray-500">
            {t('Dont have account')}{' '}
            <Link to="/register" className="text-primary-600 font-medium hover:underline">
              {t('Register')}
            </Link>
          </p>
          <p className="mt-4 text-center text-sm text-gray-500">
            <Link to="/contact" className="font-medium text-primary-600 hover:underline">{t('Need help? Contact us')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
